import {useMemo} from 'react'

import {type Theme, useTheme} from '#/alf'
import {
  type FlashBookPlayPalette,
  inkOn,
} from '#/components/flashbooks/FlashBookPlayTheme'

/**
 * The palette the shared question widgets draw in when the questions are the
 * app's, not a book's.
 *
 * A question inside a FlashBook wears the book's own paper, and the same widgets
 * are also rendered in exactly two other places: capsule play, which is an
 * immersive video viewer and is dark on a light device *by design* (that is what
 * `defaultPalette` is for), and the question card of a document, which is app
 * furniture — a card in the app's own colours, over a PDF page, in the app's
 * current mode. This is the third of those.
 *
 * It is written as a function of the theme rather than as a second hand-tuned
 * dark palette because the app's scale already inverts: `contrast_0` is the
 * ground and `contrast_1000` the ink, and the two swap ends between the modes. So
 * the whole palette can be stated once in terms of that scale, and every value
 * lands on the right side in both modes without a single `if (isDark)` choosing
 * a colour — the only branch left is the one for the *state* colours, which are
 * not the app's to invert: a success green that reads on white (#15803d) is not
 * the same colour as one that reads on black (#4ade80), because a colour that
 * reads on both is a colour that reads on neither.
 *
 * What this deliberately does *not* do is turn the widgets into FlashBook blocks:
 * the caller keeps its own framing and layout and only hands down colours. That
 * distinction is the reason this is a palette and not a theme — a theme would
 * also switch every widget into its "I am a page in a book" layout.
 */
export function documentQuestionPalette(theme: Theme): FlashBookPlayPalette {
  const scale = theme.palette
  const isDark = theme.scheme !== 'light'
  return {
    // The ground the questions are drawn on. The card itself supplies this, and
    // the widgets only ever draw *on* it, so it is the card's own colour.
    page: scale.contrast_0,
    // A raised panel inside the card: an explanation, an image answer, a board.
    surface: scale.contrast_25,
    // A soft row — an answer option the theme marks by filling rather than
    // outlining, and the fill of a button that is not yet pressable.
    surfaceAlt: scale.contrast_50,
    border: scale.contrast_100,
    text: scale.contrast_1000,
    // Reading copy inside a question: a passage to mark up, an answer's words.
    body: scale.contrast_900,
    // Quiet furniture that still has to be read: a word bank's label, a
    // placeholder, and the ink of a disabled control.
    muted: scale.contrast_600,
    // Supporting copy that has to read easily: an explanation under an answer.
    explanation: scale.contrast_700,
    inputBg: scale.contrast_50,
    inputText: scale.contrast_1000,
    accent: scale.primary_500,
    onAccent: inkOn(scale.primary_500),
    positive: isDark ? '#4ade80' : '#15803d',
    negative: isDark ? '#fb7185' : '#b91c1c',
    warning: isDark ? '#fbbf24' : '#b45309',
    // Shell and paper ink. A question card is not a book, so nothing here is
    // drawn on a shell or on ruled paper; these follow the same scale anyway so
    // that a widget reaching for one of them is never white-on-white.
    chromeText: scale.contrast_1000,
    chromeMuted: scale.contrast_700,
    chromeBorder: scale.contrast_100,
    paperTrack: scale.contrast_100,
    paperHairline: scale.contrast_100,
  }
}

export function useDocumentQuestionPalette(): FlashBookPlayPalette {
  const t = useTheme()
  return useMemo(() => documentQuestionPalette(t), [t])
}
